/**
 * serviceWorker.ts — service-worker registration + update flow for the
 * TWO independently-scoped PWAs this origin now hosts.
 *
 * 1. Roam (Responsive Workstream C, roam-responsive-pwa-spec.md §8):
 *    the staff control panel, scope "/" (frontend/public/sw.js).
 * 2. Reserve Panels (docs/reserve-panels/reserve-panels-spec.md): the
 *    unauthenticated on-device room panel at /panels, scope "/panels"
 *    (frontend/public/sw-panels.js).
 *
 * COEXISTENCE. Both are real, independent ServiceWorkerRegistrations on
 * one origin, and the platform resolves which one controls a given
 * page by LONGEST MATCHING SCOPE — a page at /panels is controlled by
 * the /panels registration (more specific) even if Roam's "/" one also
 * exists. That alone would be enough to make this safe, but this
 * module goes one step further and makes registration itself route-
 * conditional too (registerServiceWorker() below refuses to run at all
 * while the current page is under /panels, and registerPanelsServiceWorker()
 * refuses to run anywhere else): a dedicated panel device should never
 * download, install, or periodically kill-switch-poll for Roam's SW at
 * all, and a staff page should never do the same for the panels SW.
 * Getting this wrong in either direction is bad — Roam's SW ending up
 * in control of a panel, or a panel SW claiming a staff page — so
 * both are prevented at the point of registration, not left to scope
 * matching alone to sort out.
 *
 * A SECOND, LESS OBVIOUS coexistence hazard this module closes: each
 * app's kill switch used to (Roam, before Reserve Panels existed) call
 * `getRegistrations()` and unregister EVERYTHING on the origin when
 * disabled. That was harmless with only one registration ever
 * possible. With two, it would mean flipping Roam's kill switch off
 * could silently take down the panels SW too (and vice versa) — see
 * unregisterRegistrationsAtScope() below, which every disable path now
 * goes through instead.
 *
 * Registration itself is PROD-ONLY (import.meta.env.PROD, tree-shaken
 * false-branch — the Stage 19g pattern) for both apps: dev has Vite
 * HMR and no stale-SW problem, and registering a SW against the dev
 * server would fight HMR's own module graph.
 *
 * Update flow differs between the two on purpose:
 *  - Roam: sw.js never skips waiting on its own; the page posts
 *    {type:'SKIP_WAITING'} (see the 'message' listener in sw.js) the
 *    moment a new build is waiting, at load or mid-session, so every
 *    update activates silently. There is no toast and no reload:
 *    navigations are network-first and assets are content-hashed, so
 *    the page already runs the build it loaded, and a tab left on an
 *    older build is told by the app's own new-version banner (app.ts).
 *  - Reserve Panels: sw-panels.js calls self.skipWaiting() itself on
 *    every install, so a new build activates and takes control with no
 *    user gesture — there is no shell to show a toast in on this bare,
 *    unattended, fullscreen device, and nobody would ever be there to
 *    click it. registerPanelsServiceWorker() below just reloads the
 *    page once the new worker actually takes over. Documented
 *    tradeoff: this can interrupt an in-progress interaction at the
 *    panel (someone standing there mid-booking) — an idle/screensaver-
 *    aware deferred reload would remove that and is a reasonable
 *    follow-up, not built here.
 *
 * The server-side kill switch (GET /api/v1/pwa/config) is consulted in
 * BOTH places for each app: here BEFORE registering (a disabled switch
 * prevents any NEW registration — and proactively unregisters an
 * existing one), and inside each SW's own script on activate (an
 * already-installed SW self-destructs). Pre-registration matters twice
 * over: operationally, flipping the env off stops the estate growing
 * rather than only shrinking it; and in the e2e fixture, a registered
 * SW handles fetches OUTSIDE Playwright's page.route() interception (a
 * known Playwright limitation), silently breaking every spec that
 * stubs network responses — the fixture sets COS_PWA_SW_ENABLED=false
 * and COS_PWA_PANELS_SW_ENABLED=false and relies on this gate for both.
 */

// shouldRegisterServiceWorker is pulled out as a pure predicate so it's
// testable without needing to stub import.meta.env or the global
// `navigator` object — the two inputs are passed in explicitly. Shared
// by both apps; the route-specific decision (which app, if any, is
// applicable on this page) is a separate check — see isPanelsPath.
export function shouldRegisterServiceWorker(prod: boolean, supported: boolean): boolean {
	return prod && supported;
}

// shouldAnnounceUpdate is the "is this genuinely a new version, not a
// first install" check pulled out of the updatefound/statechange
// handler below so it's unit-testable on its own. Used by Roam's
// silent update flow.
export function shouldAnnounceUpdate(state: string, hasController: boolean): boolean {
	return state === "installed" && hasController;
}

export type WaitingWorkerAction = "skip-waiting" | "none";

/**
 * decideWaitingWorker is what to do about a worker sitting in `waiting`.
 *
 * Always activated at once, silently, whether it was waiting at load or
 * finished installing mid-session. sw.js changes on every build, so every
 * deploy makes one, and a toast for it reappeared on every first load after a
 * deploy. Nothing is gained by asking: navigations are network-first and
 * assets are content-hashed, so this page already runs the build it loaded,
 * and a tab left open on an older build is told by the app's own new-version
 * banner (app.ts), which compares entry hashes. No reload follows either.
 *
 * Without a controller this is a first install, not an update: nothing to do.
 */
export function decideWaitingWorker(input: { hasController: boolean }): WaitingWorkerAction {
	return input.hasController ? "skip-waiting" : "none";
}

/**
 * ControllerChangeState — the two bits the Panels registration keeps across
 * `controllerchange` events: whether this page has ever had a controller,
 * and whether we've already spent our one reload.
 */
export interface ControllerChangeState {
	hadController: boolean;
	reloaded: boolean;
}

/**
 * decideControllerChange is the "should this `controllerchange` reload the
 * page?" rule, pulled out of BOTH registration handlers below so the two
 * unit-testable without faking navigator.serviceWorker. Only the Panels
 * registration uses it: Roam never reloads on `controllerchange`.
 *
 * Three cases:
 *   1. Already reloaded once → never again (a later controllerchange for an
 *      unrelated reason must not put the page in a reload loop).
 *   2. No controller yet → this is the page's FIRST-EVER claim, not an
 *      update. Both sw.js and sw-panels.js call clients.claim() on activate,
 *      so a brand-new install fires controllerchange on a page that is
 *      already correctly rendered. Adopt the controller, don't reload.
 *   3. A controller is being REPLACED → a genuine update (sw-panels.js
 *      skips waiting itself). Reload, once.
 */
export function decideControllerChange(state: ControllerChangeState): {
	reload: boolean;
	next: ControllerChangeState;
} {
	if (state.reloaded) return { reload: false, next: state };
	if (!state.hadController) return { reload: false, next: { ...state, hadController: true } };
	return { reload: true, next: { ...state, hadController: true, reloaded: true } };
}

// isPanelsPath is true for the Reserve Panels route and anything nested
// under it (the spec's §5.5(b) "kiosk link" flavour, /panels/k/<token>,
// isn't a registered route yet, but this stays correct if it becomes
// one) — false for everything else, including a hypothetical unrelated
// route that merely starts with the same five letters (e.g. /panelsfoo).
export function isPanelsPath(pathname: string): boolean {
	return pathname === "/panels" || pathname.startsWith("/panels/");
}

// killSwitchAllows decides whether the server-side config permits a NEW
// registration for the given kill-switch field (default "sw_enabled",
// Roam's own field; pass "panels_sw_enabled" for the panels app). Fail-
// OPEN on anything but that field being explicitly `false` — a flaky
// config fetch (or an older backend without the route, or without the
// panels-specific field yet) must not strip either PWA from every
// visitor; each SW's own activate-time check remains the authoritative
// self-destruct for an installed worker.
export function killSwitchAllows(config: unknown, key: string = "sw_enabled"): boolean {
	return !(typeof config === "object" && config !== null && (config as Record<string, unknown>)[key] === false);
}

/**
 * Unregisters ONLY the ServiceWorkerRegistration whose scope exactly
 * matches `scopePath` (e.g. "/" or "/panels") — never every
 * registration on the origin. See the header note above: with two
 * independently-scoped PWAs sharing this origin, a kill switch that
 * unregisters indiscriminately would let flipping one app's switch
 * take the other one's SW down too.
 */
export async function unregisterRegistrationsAtScope(scopePath: string): Promise<void> {
	const target = new URL(scopePath, window.location.origin).href;
	const regs = await navigator.serviceWorker.getRegistrations();
	await Promise.all(regs.filter((r) => r.scope === target).map((r) => r.unregister()));
}

/**
 * wireRoamUpdates silently activates every new Roam worker: one already
 * waiting at registration, and one that finishes installing mid-session. It
 * takes the registration and the container's pieces as arguments so it is
 * testable without import.meta.env.PROD.
 *
 * No reload follows, on `controllerchange` or otherwise: navigations are
 * network-first and assets are content-hashed, so this page already runs the
 * current build, and a reload at load would break one-shot pages mid-boot
 * (the GitHub OAuth callback spends its single-use code before this fires).
 */
export function wireRoamUpdates(
	reg: Pick<ServiceWorkerRegistration, "waiting" | "installing" | "addEventListener">,
	container: Pick<ServiceWorkerContainer, "controller">,
): void {
	const activate = (worker: ServiceWorker | null): void => {
		if (!worker) return;
		if (decideWaitingWorker({ hasController: !!container.controller }) === "skip-waiting") {
			worker.postMessage({ type: "SKIP_WAITING" });
		}
	};

	activate(reg.waiting);

	reg.addEventListener("updatefound", () => {
		const installing = reg.installing;
		if (!installing) return;
		installing.addEventListener("statechange", () => {
			if (shouldAnnounceUpdate(installing.state, !!container.controller)) activate(installing);
		});
	});
}

export async function registerServiceWorker(): Promise<void> {
	if (!shouldRegisterServiceWorker(import.meta.env.PROD, typeof navigator !== "undefined" && "serviceWorker" in navigator)) {
		return;
	}
	// Roam is route-agnostic everywhere EXCEPT /panels — a dedicated
	// panel device should never register, poll the kill switch for, or
	// precache Roam's static shell at all. See the header note.
	if (typeof window !== "undefined" && isPanelsPath(window.location.pathname)) {
		return;
	}

	try {
		// Consult the kill switch BEFORE registering (see the header note).
		// Same-origin, unauthenticated; any fetch/parse failure fails open.
		let allowed = true;
		try {
			const cfgRes = await fetch("/api/v1/pwa/config");
			if (cfgRes.ok) allowed = killSwitchAllows(await cfgRes.json());
		} catch {
			/* fail open */
		}
		if (!allowed) {
			// Disabled: never register, and clean up any worker a prior
			// visit installed so the disabled state converges everywhere —
			// but ONLY Roam's own ("/") registration, never the panels one.
			await unregisterRegistrationsAtScope("/");
			return;
		}

		const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
		wireRoamUpdates(reg, navigator.serviceWorker);
	} catch {
		// Registration is a progressive enhancement, never a dependency —
		// an unsupported context, a policy block, or a flaky first fetch
		// of sw.js must not break the app.
	}
}

/**
 * Reserve Panels' own registration — the mirror of registerServiceWorker()
 * above, scoped to /panels only. See the header note for why this is a
 * separate function rather than a parameterised version of Roam's: the
 * update flow (the worker skips waiting itself, and the page reloads once it
 * takes over) is genuinely different, not just differently-scoped.
 *
 * Called from cos-panels-app's connectedCallback — a component that
 * only ever mounts while the page IS /panels, so the isPanelsPath
 * check below is defensive (mirroring registerServiceWorker()'s own
 * defensive checks) rather than load-bearing at the one real call site.
 */
export async function registerPanelsServiceWorker(): Promise<void> {
	if (!shouldRegisterServiceWorker(import.meta.env.PROD, typeof navigator !== "undefined" && "serviceWorker" in navigator)) {
		return;
	}
	if (typeof window === "undefined" || !isPanelsPath(window.location.pathname)) {
		return;
	}

	try {
		let allowed = true;
		try {
			const cfgRes = await fetch("/api/v1/pwa/config");
			if (cfgRes.ok) allowed = killSwitchAllows(await cfgRes.json(), "panels_sw_enabled");
		} catch {
			/* fail open */
		}
		if (!allowed) {
			await unregisterRegistrationsAtScope("/panels");
			return;
		}

		// Snapshot whether this page was ALREADY controlled by a worker
		// before this call — the same "is this a genuine update, not the
		// page's first-ever claim" signal shouldAnnounceUpdate uses for
		// Roam, just checked at registration time instead of on the
		// installing worker's statechange. sw-panels.js calls
		// self.skipWaiting() unconditionally on install, so a brand-new
		// install on a previously-uncontrolled page ALSO fires
		// controllerchange once claim() runs — that transition needs no
		// reload (the page is already correctly rendered); only a LATER
		// controllerchange, replacing an existing controller, is a real
		// update worth reloading for.
		const hadController = !!navigator.serviceWorker.controller;

		await navigator.serviceWorker.register("/sw-panels.js", { scope: "/panels" });

		let ccState: ControllerChangeState = { hadController, reloaded: false };
		navigator.serviceWorker.addEventListener("controllerchange", () => {
			const { reload, next } = decideControllerChange(ccState);
			ccState = next;
			if (reload) window.location.reload();
		});
	} catch {
		// Same progressive-enhancement posture as registerServiceWorker().
	}
}
